import classNames from "classnames";
import React, {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { Avatar } from "./Avatar";
import { AvatarStackProps } from "./Avatar.types";
import { AvatarCount } from "./AvatarCount";
import { AvatarPlaceholder } from "./AvatarPlaceholder";
import { MembersList } from "./MembersList";

const MEMBERS_LIST_WIDTH = 225;
const MEMBERS_LIST_OFFSET = 8;
const MEMBERS_LIST_CLOSE_DELAY_MS = 120;

export const AvatarStack: FC<AvatarStackProps> = ({
  items,
  size = "md",
  maxItems,
  fillPlaceholders = true,
  showMembersList = true,
  membersListTitle,

  avatarClassNames,
  avatarPlaceholderClassNames,
  avatarCountClassNames,
}) => {
  const stackRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isMembersListOpen, setIsMembersListOpen] = useState(false);
  const [membersListStyle, setMembersListStyle] =
    useState<CSSProperties | null>(null);

  const limit = maxItems ?? 5;

  const hasLimit = typeof maxItems === "number";
  const shouldShowCount = items.length > limit;

  const visibleItemsLimit = shouldShowCount ? limit - 1 : limit;

  const visibleItems = items.slice(0, visibleItemsLimit);

  const hiddenItemsCount = shouldShowCount
    ? items.length - visibleItems.length
    : 0;

  const placeholdersCount =
    fillPlaceholders && hasLimit && !shouldShowCount
      ? Math.max(limit - visibleItems.length, 0)
      : 0;

  const shouldShowMembersList = showMembersList && items.length > 0;

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const updateMembersListPosition = useCallback(() => {
    const stackElement = stackRef.current;

    if (!stackElement) {
      return;
    }

    const rect = stackElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const left = Math.min(
      Math.max(MEMBERS_LIST_OFFSET, rect.right - MEMBERS_LIST_WIDTH),
      viewportWidth - MEMBERS_LIST_WIDTH - MEMBERS_LIST_OFFSET,
    );

    setMembersListStyle({
      left,
      position: "fixed",
      top: rect.bottom,
      zIndex: 9999,
    });
  }, []);

  const openMembersList = useCallback(() => {
    if (!shouldShowMembersList) {
      return;
    }

    clearCloseTimer();
    updateMembersListPosition();
    setIsMembersListOpen(true);
  }, [clearCloseTimer, shouldShowMembersList, updateMembersListPosition]);

  const closeMembersList = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setIsMembersListOpen(false);
    }, MEMBERS_LIST_CLOSE_DELAY_MS);
  }, [clearCloseTimer]);

  useEffect(() => {
    if (!isMembersListOpen) {
      return undefined;
    }

    window.addEventListener("resize", updateMembersListPosition);
    window.addEventListener("scroll", updateMembersListPosition, true);

    return () => {
      window.removeEventListener("resize", updateMembersListPosition);
      window.removeEventListener("scroll", updateMembersListPosition, true);
    };
  }, [isMembersListOpen, updateMembersListPosition]);

  useEffect(() => {
    return clearCloseTimer;
  }, [clearCloseTimer]);

  const membersListPortal =
    shouldShowMembersList &&
    isMembersListOpen &&
    membersListStyle &&
    typeof document !== "undefined"
      ? createPortal(
          <div
            style={membersListStyle}
            onMouseEnter={openMembersList}
            onMouseLeave={closeMembersList}
            className={"pt-2"}>
            <MembersList items={items} title={membersListTitle} />
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={stackRef}
      className={"relative inline-flex"}
      onMouseEnter={openMembersList}
      onMouseLeave={closeMembersList}>
      <div className={"flex"}>
        {visibleItems.map((item) => (
          <Avatar
            key={item.id}
            {...item}
            size={size}
            avatarClassNames={classNames(
              "ring-2 ring-white -mr-2",
              avatarClassNames,
            )}
          />
        ))}

        {Array.from({ length: placeholdersCount }).map((_, index) => (
          <AvatarPlaceholder
            key={`avatar-placeholder-${index}`}
            size={size}
            avatarPlaceholderClassNames={classNames(
              "-mr-2",
              avatarPlaceholderClassNames,
            )}
          />
        ))}

        {shouldShowCount && (
          <AvatarCount
            count={hiddenItemsCount}
            size={size}
            avatarCountClassNames={classNames(
              "ring-2 ring-gray-300 -mr-2",
              avatarCountClassNames,
            )}
          />
        )}
      </div>

      {membersListPortal}
    </div>
  );
};
